/**
 * Service Context Hooks - React Integration for Service Registry
 * Provides service access hooks throughout the React component tree
 * Separated from ServiceProvider for optimal React Fast Refresh performance
 */

import { useContext } from 'react';
import { ServiceContext } from './ServiceContextDefinition';
import { serviceRegistry } from '@/services/core/service-registry';
import type { IService } from '@/services/core/service-registry';
import { logger } from "@/lib/logger";

/**
 * Hook to get a specific service instance from the registry
 * Note: Returns synchronously if service is already initialized, null otherwise
 * Usage: const myService = useService<MyServiceType>('my-service');
 */
export const useService = <T extends IService>(serviceName: string): T | null => {
  const context = useContext(ServiceContext);
  if (!context) {
    logger.warn(`[useService] Service context not available for '${serviceName}'. Make sure ServiceProvider is in the component tree.`);
    return null;
  }

  try {
    // Use getSync for synchronous access - service must be pre-initialized
    return context.registry.getSync<T>(serviceName);
  } catch (error) {
    // Service not initialized yet, return null
    // Only log in development to avoid spamming console
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`[useService] Service '${serviceName}' not ready yet`, { error });
    }
    return null;
  }
};

/**
 * Hook to get a specific service instance from the registry (async)
 * This will initialize the service if not already initialized
 * Usage: const myService = await useServiceAsync<MyServiceType>('my-service');
 */
export const useServiceAsync = <T extends IService>(serviceName: string): Promise<T | null> => {
  const context = useContext(ServiceContext);
  if (!context) {
    logger.warn('useServiceAsync must be used within a ServiceProvider');
    return Promise.resolve(null);
  }
  
  return context.registry.get<T>(serviceName).catch(error => {
    logger.error(`❌ Failed to get service: ${serviceName}`, { error: error });
    return null;
  });
};

/**
 * Hook to get the service registry instance
 * Usage: const registry = useServiceRegistry();
 */
export const useServiceRegistry = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServiceRegistry must be used within a ServiceProvider');
  }
  
  return context.registry;
};

/**
 * Direct access to service instances (for non-React contexts, synchronous)
 * Usage: const service = getServiceInstance<MyServiceType>('my-service');
 */
export const getServiceInstance = <T extends IService>(serviceName: string): T | null => {
  if (!serviceRegistry.has(serviceName)) {
    logger.warn(`Service '${serviceName}' is not registered`);
    return null;
  }
  
  try {
    return serviceRegistry.getSync<T>(serviceName);
  } catch (_error) {
    // Service not initialized yet
    return null;
  }
};

/**
 * Direct access to service instances (for non-React contexts, asynchronous)
 * Usage: const service = await getServiceInstanceAsync<MyServiceType>('my-service');
 */
export const getServiceInstanceAsync = async <T extends IService>(serviceName: string): Promise<T | null> => {
  if (!serviceRegistry.has(serviceName)) {
    logger.warn(`Service '${serviceName}' is not registered`);
    return null;
  }
  
  try {
    return await serviceRegistry.get<T>(serviceName);
  } catch (error) {
    logger.error(`❌ Failed to get service instance: ${serviceName}`, { error: error });
    return null;
  }
};

/**
 * Check if a service is registered
 * Usage: if (isServiceRegistered('my-service')) { ... }
 */
export const isServiceRegistered = (serviceName: string): boolean => {
  return serviceRegistry.has(serviceName);
};

/**
 * Get all registered service names
 * Usage: const services = getRegisteredServices();
 */
export const getRegisteredServices = (): string[] => {
  return serviceRegistry.getStats().services;
}; 